/**
 * FormbricksHandler - Formbricks implementation of FeedbackServiceHandler
 *
 * Wraps the @formbricks/js SDK singleton. The constructor calls setup() so
 * only one instance should ever be created (done by useFeedback).
 *
 * Switching providers:
 *   1. Create a new handler implementing FeedbackServiceHandler
 *   2. In useFeedback.ts, replace `new FormbricksHandler(...)` with the new handler
 *   3. Update .env with the new provider's credentials
 *
 * Environment variables:
 *   FORMBRICKS_ENVIRONMENT_ID  - Formbricks environment ID from Settings > Setup Checklist
 *   FORMBRICKS_APP_URL         - Formbricks instance URL (default: https://app.formbricks.com)
 */

import formbricks from '@formbricks/js';
import type { FeedbackServiceHandler } from './FeedbackService';

export interface FormbricksConfig {
  environmentId: string;
  appUrl: string;
}

export class FormbricksHandler implements FeedbackServiceHandler {
  constructor(config: FormbricksConfig) {
    if (typeof window === 'undefined') return;

    if (!config.environmentId) {
      console.warn('[Feedback] FormbricksHandler: missing environmentId, skipping setup');
      return;
    }
    formbricks.setup({
      environmentId: config.environmentId,
      appUrl: config.appUrl,
    });
  }

  identify(userId: string, attributes?: Record<string, string>) {
    // Fire-and-forget: SDK queues calls internally until setup resolves
    formbricks.setUserId(userId);
    if (attributes) {
      formbricks.setAttributes(attributes);
    }
  }

  reset() {
    formbricks.logout();
  }

  registerRouteChange() {
    formbricks.registerRouteChange();
  }

  setLanguage(language: string) {
    formbricks.setLanguage(language);
  }
}
